import { TextButton2 } from "common/components";
import { textStyleButton2 } from "common/components/Style/new/textStyle";
import { colors } from "common/const/colors";
import { largeComponentHeight } from "common/const/const";
import { GlobalKeyListener } from "common/utils/globalKeyListener";
import styled from "styled-components";

export const BaseButton = styled.button.attrs({ type: "button" })`
  display: flex;
  width: auto;
  min-width: 0;
  height: 100%;
  padding: 0.75rem;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  cursor: pointer;
  box-sizing: border-box;
`;

const getBorder = ({ borderType, borderColor }) =>
  `1px ${borderType ?? "solid"} ${borderColor ?? "transparent"}`;

export const DefaultButtonWrapper = styled(BaseButton)`
  ${textStyleButton2}
  flex: ${({ flex }) => flex};
  width: ${({ width }) => width || "inherit"};
  height: ${({ height }) => height || "inherit"};
  border-radius: var(--border-radius-8);
  background-color: ${({ disabled, fillColor }) =>
    disabled ? colors.Gray : (fillColor ?? "transparent")};
  border: ${getBorder};
  color: ${({ textColor }) => textColor ?? colors.DarkNavy};
  background-image: ${({ src }) => `url(${src})`};
  background-size: cover;
  background-position: center;

  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:active {
    background-color: ${({ disabled, activeColor, fillColor }) =>
      disabled ? fillColor : activeColor || fillColor};
    color: ${({ activeTextColor, textColor }) => activeTextColor ?? textColor};
  }
`;

export const DefaultButton = ({ triggerKeys, children, ...rest }) => {
  return (
    <DefaultButtonWrapper {...rest}>
      {triggerKeys && (
        <GlobalKeyListener
          triggerKeys={triggerKeys}
          onTrigger={rest.onClick}
          enabled={!rest.disabled}
        />
      )}
      {children}
    </DefaultButtonWrapper>
  );
};

DefaultButton.White = ({
  text,
  flex,
  width = "100%",
  height = largeComponentHeight,
  onClick,
  disabled,
  outlineColor,
  children,
  ...rest
}) => {
  return (
    <DefaultButton
      flex={flex}
      width={width}
      height={height}
      disabled={disabled}
      onClick={onClick}
      fillColor={colors.White}
      borderColor={outlineColor ?? (disabled ? colors.Gray : colors.MainBlue)}
      textColor={outlineColor ?? (disabled ? colors.White : colors.MainBlue)}
      activeColor={outlineColor ?? colors.MainBlue}
      activeTextColor={colors.White}
      {...rest}>
      {text}
      {children}
    </DefaultButton>
  );
};

DefaultButton.Blue = ({
  text,
  flex,
  width = "100%",
  height = largeComponentHeight,
  onClick,
  disabled,
  children,
  ...rest
}) => {
  return (
    <DefaultButton
      flex={flex}
      width={width}
      height={height}
      disabled={disabled}
      onClick={onClick}
      fillColor={disabled ? colors.Gray : colors.MainBlue}
      activeColor={colors.MainBlueDark}
      textColor={colors.White}
      {...rest}>
      {text}
      {children}
    </DefaultButton>
  );
};

export const SelectableButton = ({
  onSelect,
  defaultColor,
  selectedColor,
  secondary,
  selected,
  disabled,
  children,
  ...rest
}) => {
  const fillColor = disabled
    ? colors.White
    : selected
      ? secondary
        ? colors.Gray
        : colors.MainBlue
      : colors.White;

  const textColor = disabled
    ? colors.Gray
    : selected
      ? (selectedColor ?? colors.White)
      : (defaultColor ?? (secondary ? colors.LightNavy : colors.MainBlue));

  return (
    <DefaultButton
      fillColor={fillColor}
      borderColor={textColor}
      textColor={textColor}
      onClick={disabled ? undefined : onSelect}
      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      {...rest}>
      {children}
    </DefaultButton>
  );
};

SelectableButton.ByText = ({
  text,
  onSelect,
  defaultColor,
  selectedColor,
  secondary,
  selected,
  disabled,
  ...rest
}) => (
  <SelectableButton
    defaultColor={defaultColor}
    selectedColor={selectedColor}
    secondary={secondary}
    selected={selected}
    onSelect={onSelect}
    disabled={disabled}
    {...rest}>
    <TextButton2>{text}</TextButton2>
  </SelectableButton>
);
